import ReactSelect from '@components/atoms/ReactSelect';
import { DOCUMENT_CATEGORIES_OPTIONS } from '../../constants';
import { ProjectDocumentsProps } from './types';
import { useDocumentManager } from './hooks/useDocumentManager';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { DocumentItem } from './components/DocumentItem';
import { Modal } from '@components/molecules/Modal';
import { useState } from 'react';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { useTranslation } from '@hooks/useTranslation';

export const ProjectDocumentsModal = ({
  isOpen,
  onClose,
  documents,
  category,
  setCategory,
  handleFileChange,
}: ProjectDocumentsProps) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.documents;
  const [error, setError] = useState<string | null>(null);
  const { isUploading, handleDrop, localDocuments } =
    useDocumentManager({
      category,
      documents,
      handleFileChange,
    });

  const handleClose = () => {
    setCategory(null);
    setError(null);
    onClose();
  };

  const handleCategoryChange = (value: any) => {
    const newCategory = value?.value as string;
    setCategory(newCategory);
    if (newCategory) {
      setError(null);
    }
  };

  const handleFilesChange = (files: File[]) => {
    if (!category) {
      setError(t.pleaseSelectCategory);
      return;
    }
    // Files are uploaded immediately, pass them to handleDrop
    if (files.length > 0) {
      handleDrop(files);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      confirmText={t.done}
      handleConfirm={handleClose}
      title={t.manageDocumentsTitle}
      description={t.manageDocumentsDescription}>
      <div className="w-full max-w-xl mx-auto">
        <FormGroup
          error={error || undefined}
          htmlFor="category"
          label={t.categoryFieldLabel}>
          <ReactSelect
            error={!!error}
            isClearable={false}
            options={DOCUMENT_CATEGORIES_OPTIONS}
            placeholder={t.selectCategoryPlaceholder}
            onChange={handleCategoryChange}
            value={
              category
                ? DOCUMENT_CATEGORIES_OPTIONS.find(
                    (option) => option.value === category,
                  )
                : undefined
            }
          />
        </FormGroup>

        <FileDropZone
          files={[]}
          onFilesChange={handleFilesChange}
          multiple={true}
          acceptedTypes="all"
          disabled={!category}
          isUploading={isUploading}
          title={!category ? t.selectCategoryFirst : t.dropDocumentsHere}
          subtitle={t.documentsSubtitle}
          showFileList={false}
          className="mt-6"
        />

        {localDocuments?.some((d) => d.status === 'uploading') ? (
          <ul className="mt-4 space-y-2">
            {localDocuments
              .filter(
                (doc) =>
                  doc.status === 'uploading' || doc.status === 'completed',
              )
              .map((doc) => (
                <DocumentItem key={doc.documentId || doc.name} document={doc} />
              ))}
          </ul>
        ) : null}
      </div>
    </Modal>
  );
};
